import * as React from 'react'
const { createContext, useEffect, useMemo, useState, useRef, useCallback } = React
import { TeerApi } from '../api/teerApi'
import {
  BillingConfig,
  BillingPortalOptions,
  BillingPortalSession,
  CheckoutOptions,
  CheckoutSession,
  Customer,
  Subscription,
  TeerContextValue,
  UsageData,
} from '../types'

// Create the context with a default value
export const TeerContext = createContext<TeerContextValue | undefined>(undefined)

export interface TeerProviderProps {
  /**
   * Provider configuration
   */
  publishableKey: string

  /**
   * Optional customer ID
   */
  customerId?: string

  /**
   * Optional customer email
   */
  customerEmail?: string

  /**
   * Success URL for checkout redirects
   */
  successUrl?: string

  /**
   * Cancel URL for checkout redirects
   */
  cancelUrl?: string

  /**
   * Whether to fetch billing configuration on mount
   */
  fetchOnMount?: boolean

  /**
   * Children components
   */
  children: React.ReactNode
}

/**
 * Teer Provider Component
 */
export const TeerProvider: React.FC<TeerProviderProps> = ({
  publishableKey,
  customerId,
  customerEmail,
  successUrl,
  cancelUrl,
  fetchOnMount = true,
  children,
}: TeerProviderProps) => {
  // Create API client
  const api = useMemo(() => new TeerApi(publishableKey), [publishableKey])

  // State
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isReady, setIsReady] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])

  // Refs to track request state and prevent duplicate requests
  const isFetchingRef = useRef<boolean>(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Stable references to props for effect dependencies
  const publishableKeyRef = useRef<string>(publishableKey)
  const customerIdRef = useRef<string | undefined>(customerId)

  // Update refs when props change
  useEffect(() => {
    publishableKeyRef.current = publishableKey
    customerIdRef.current = customerId
  }, [publishableKey, customerId])

  // Fetch billing configuration with abort controller and duplicate request prevention
  const fetchBillingConfig = useCallback(async () => {
    // Prevent duplicate requests
    if (isFetchingRef.current) {
      return
    }

    try {
      // Cancel any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController()
      isFetchingRef.current = true

      setIsLoading(true)
      setError(null)

      // Use the signal for fetch requests
      const signal = abortControllerRef.current.signal

      const config = await api.getBillingConfig(signal)

      // Check if request was aborted
      if (signal.aborted) return

      setBillingConfig(config)

      // If customerId is provided, fetch customer and subscriptions
      if (customerIdRef.current) {
        const customerData = await api.getCustomer(customerIdRef.current, signal)

        // Check if request was aborted
        if (signal.aborted) return

        setCustomer(customerData)

        const subscriptionsData = await api.getSubscriptions(customerIdRef.current, signal)

        // Check if request was aborted
        if (signal.aborted) return

        setSubscriptions(subscriptionsData)
      } else {
        // Clear customer data if no customerId
        setCustomer(null)
        setSubscriptions([])
      }

      setIsReady(true)
    } catch (err) {
      // Don't update state if the request was aborted
      if (err instanceof DOMException && err.name === 'AbortError') {
        return
      }

      setError(err instanceof Error ? err : new Error('Failed to fetch billing configuration'))
    } finally {
      isFetchingRef.current = false
      setIsLoading(false)
    }
  }, [api]) // Only depend on the API client

  // Refetch billing configuration - exposed in the context
  const refetch = useCallback(async () => {
    await fetchBillingConfig()
  }, [fetchBillingConfig])

  // Create checkout session
  const checkout = useCallback(
    async (options: CheckoutOptions | string): Promise<CheckoutSession> => {
      // Use a local loading state to avoid conflicts with other operations
      let localLoading = false
      // Create a local abort controller for this operation
      const abortController = new AbortController()
      const signal = abortController.signal

      try {
        // Only set global loading if no other operation is in progress
        if (!isFetchingRef.current) {
          setIsLoading(true)
          localLoading = true
        }

        // If options is a string, assume it's a priceId
        const checkoutOptions: CheckoutOptions = typeof options === 'string' ? { priceId: options } : options

        // Add default values if not provided
        if (!checkoutOptions.successUrl && successUrl) {
          checkoutOptions.successUrl = successUrl
        }

        if (!checkoutOptions.cancelUrl && cancelUrl) {
          checkoutOptions.cancelUrl = cancelUrl
        }

        if (!checkoutOptions.customerId && customerIdRef.current) {
          checkoutOptions.customerId = customerIdRef.current
        }

        if (!checkoutOptions.customerEmail && customerEmail) {
          checkoutOptions.customerEmail = customerEmail
        }

        const session = await api.createCheckoutSession(checkoutOptions, signal)

        // Check if the request was aborted
        if (signal.aborted) {
          throw new DOMException('Request aborted', 'AbortError')
        }

        // Redirect to checkout page
        if (typeof window !== 'undefined' && session.url) {
          window.location.href = session.url
        }

        return session
      } catch (err) {
        // Don't update state if the request was aborted
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err
        }

        const error = err instanceof Error ? err : new Error('Failed to create checkout session')
        setError(error)
        throw error
      } finally {
        // Clean up abort controller
        abortController.abort()

        // Only reset global loading if we set it
        if (localLoading) {
          setIsLoading(false)
        }
      }
    },
    [api, successUrl, cancelUrl, customerEmail]
  )

  // Create billing portal session
  const billingPortal = useCallback(
    async (options?: BillingPortalOptions): Promise<BillingPortalSession> => {
      // Use a local loading state to avoid conflicts with other operations
      let localLoading = false
      // Create a local abort controller for this operation
      const abortController = new AbortController()
      const signal = abortController.signal

      try {
        // Only set global loading if no other operation is in progress
        if (!isFetchingRef.current) {
          setIsLoading(true)
          localLoading = true
        }

        const portalOptions: BillingPortalOptions = options || {}

        // Add default values if not provided
        if (!portalOptions.customerId && customerIdRef.current) {
          portalOptions.customerId = customerIdRef.current
        }

        if (!portalOptions.returnUrl && typeof window !== 'undefined') {
          portalOptions.returnUrl = window.location.href
        }

        const session = await api.createBillingPortalSession(portalOptions, signal)

        // Check if the request was aborted
        if (signal.aborted) {
          throw new DOMException('Request aborted', 'AbortError')
        }

        // Redirect to billing portal
        if (typeof window !== 'undefined' && session.url) {
          window.location.href = session.url
        }

        return session
      } catch (err) {
        // Don't update state if the request was aborted
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err
        }

        const error = err instanceof Error ? err : new Error('Failed to create billing portal session')
        setError(error)
        throw error
      } finally {
        // Clean up abort controller
        abortController.abort()

        // Only reset global loading if we set it
        if (localLoading) {
          setIsLoading(false)
        }
      }
    },
    [api]
  )

  // Report usage
  const reportUsage = useCallback(
    async (subscriptionItemId: string, quantity: number): Promise<UsageData> => {
      // Use a local loading state to avoid conflicts with other operations
      let localLoading = false
      // Create a local abort controller for this operation
      const abortController = new AbortController()
      const signal = abortController.signal

      try {
        // Only set global loading if no other operation is in progress
        if (!isFetchingRef.current) {
          setIsLoading(true)
          localLoading = true
        }

        const result = await api.reportUsage(subscriptionItemId, quantity, signal)

        // Check if the request was aborted
        if (signal.aborted) {
          throw new DOMException('Request aborted', 'AbortError')
        }

        return result
      } catch (err) {
        // Don't update state if the request was aborted
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err
        }

        const error = err instanceof Error ? err : new Error('Failed to report usage')
        setError(error)
        throw error
      } finally {
        // Clean up abort controller
        abortController.abort()

        // Only reset global loading if we set it
        if (localLoading) {
          setIsLoading(false)
        }
      }
    },
    [api]
  )

  // Cleanup function for abort controller
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Fetch billing configuration on mount or when key dependencies change
  useEffect(() => {
    if (fetchOnMount) {
      fetchBillingConfig()
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchOnMount, fetchBillingConfig])

  // Context value - memoized to prevent unnecessary re-renders
  const contextValue = useMemo<TeerContextValue>(
    () => ({
      isLoading,
      isReady,
      error,
      billingConfig,
      customer,
      subscriptions,
      refetch,
      checkout,
      billingPortal,
      reportUsage,
    }),
    [isLoading, isReady, error, billingConfig, customer, subscriptions, refetch, checkout, billingPortal, reportUsage]
  )

  return <TeerContext.Provider value={contextValue}>{children}</TeerContext.Provider>
}
